import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Badge,
  Alert
} from "react-bootstrap";
import { CBadge } from '@coreui/react'
import Select from 'react-select';
import { useSelector, useDispatch } from "react-redux";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import CommonModal from "components/ui/CommonModal";
import ActionButtons from "components/ui/ActionButtons";
import AddButton from "components/ui/AddButton";
import { GRADIENTS } from "utils/theme";
import usePagination from "hooks/usePagination";
import Pagination from "components/ui/Pagination";
import TableFilter from "components/ui/TableFilter";
import './categories.css';
import { useNavigate } from "react-router";

function Categories() {
  const { fetchData } = Useaxios();
   const navigate = useNavigate() 

  const fetchCategories = useCallback(async (params) => {
    const res = await fetchData({
      url: API.getCategory,
      method: 'GET',
      params,
      showMessage: false
    });
    return res;
  }, []);

  const {
    data: categories,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    filters,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    updateFilters,
    clearFilters,
    refresh
  } = usePagination(fetchCategories, { name: '', status: '' });

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', machineIds: [], status: 'online' });
  const [machineOptions, setMachineOptions] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingCategory, setViewingCategory] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Category name is required";
    }
    if (formData.name.length < 2) {
      newErrors.name = "Category name must be at least 2 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check for duplicate category name
    const isDuplicate = categories.some(cat => 
      cat.name.toLowerCase() === formData.name.trim().toLowerCase() && 
      (!editingCategory || cat._id !== editingCategory._id)
    );

    if (isDuplicate) {
      const Swal = require('sweetalert2');
      Swal.fire({
        icon: 'error',
        title: 'Duplicate Category',
        text: 'Category with this name already exists!',
        confirmButtonColor: '#d33',
      });
      return;
    }

    setSubmitLoading(true);
    const submitData = {
      ...formData,
      machineIds: formData.machineIds.map(option => option.value)
    };

    try {
      if (editingCategory) {
        // Update existing category
        const res = await fetchData({
          url: `${API.updateCategory}/${editingCategory._id}`,
          method: "PUT",
          data: submitData
        });
        if (res.success) {
          refresh();
          resetForm();
        }
      } else {
        // Create new category
        const res = await fetchData({
          url: API.createCategory,
          method: "POST",
          data: submitData
        });
        if (res.success) {
          refresh();
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error saving category:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    const selectedMachines = category.machineIds?.map(id =>
      machineOptions.find(option => option.value === id)
    ).filter(Boolean) || [];

    setFormData({
      name: category.name,
      machineIds: selectedMachines,
      status: category.status || 'online'
    });
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = (category) => {
    const Swal = require('sweetalert2');
    Swal.fire({
      title: 'Delete Category?',
      html: `Are you sure you want to delete <strong>"${category.name}"</strong>?<br><small class="text-muted">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="fas fa-trash me-2"></i>Yes, delete it!',
      cancelButtonText: '<i class="fas fa-times me-2"></i>Cancel',
      customClass: {
        confirmButton: 'custom-delete-btn',
        cancelButton: 'shadow-none border-0'
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetchData({
            url: `${API.deleteCategory(category._id)}`,
            method: "DELETE"
          });
          if (res.success) {
            refresh();
            Swal.fire('Deleted!', 'Category has been deleted.', 'success');
          }
        } catch (error) {
          Swal.fire('Error!', 'Failed to delete category.', 'error');
        }
      }
    });
  };

  const handleView = (category) => {
    setViewingCategory(category);
    setShowViewModal(true);
  };



  const resetForm = () => {
    setFormData({ name: '', machineIds: [], status: 'online' });
    setEditingCategory(null);
    setShowModal(false);
    setErrors({});
  };



  const getAllMachines = async () => {
    try {
      const res = await fetchData({
        url: API.getMachine,
        method: "GET",
        showMessage: false,
        params: {
          limit: 1000
        }
      });
      console.log(res.data)
      if (res.success) {
        const options = res.data.data.map(machine => ({
          value: machine.machineId,
          label: `${machine.name} (${machine.machineId})`
        }));
        setMachineOptions(options);
      }
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  useEffect(() => {
    getAllMachines();
  }, []);

  const filterFields = [
    {
      key: 'name',
      label: 'Category Name',
      type: 'text',
      placeholder: 'Search by name...'
    },

  ];

  return (
    <>
      <Container fluid className="py-4">
        <Row>
          <Col md="12">
            <Card className="shadow border-0 rounded-3 overflow-hidden">
              <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-0" style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                      <i className="fas fa-tags me-2"></i>
                      <span className="d-none d-sm-inline">Categories Management</span>
                      <span className="d-inline d-sm-none">Categories</span>
                    </h4>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="d-flex align-items-center"
                    >
                      <i className="fas fa-filter me-1"></i>
                      <span className="d-none d-md-inline">Filters</span>
                      {Object.values(filters).filter(v => v).length > 0 && (
                        <Badge bg="danger" className="ms-2">{Object.values(filters).filter(v => v).length}</Badge>
                      )}
                    </Button>
                    <AddButton
                      onClick={() => setShowModal(true)}
                      icon="fas fa-plus-circle"
                      variant="light"
                    >
                      <span className="d-none d-md-inline">Add New Category</span>
                      <span className="d-inline d-md-none">Add</span>
                    </AddButton>
                  </div>
                </div>
              </div>

              {showFilters && (
                <div className="px-3 pt-3 border-bottom">
                  <TableFilter
                    filterConfig={filterFields}
                    filters={filters}
                    onFilterChange={updateFilters}
                    onClearFilters={clearFilters}
                  />
                </div>
              )}

              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading categories...</p>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="display-1 text-muted mb-3">
                      <i className="fas fa-tags"></i>
                    </div>
                    <h4 className="text-muted mb-3">No Categories Found</h4>
                    <p className="text-muted mb-4">Get started by creating your first category</p>
                    <Button
                      variant="primary"
                      onClick={() => setShowModal(true)}
                      className="px-4"
                    >
                      <i className="fas fa-plus me-2"></i>Create Category
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0 categories-table">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0 ps-4">Category Name</th>
                          <th className="border-0 text-center">Product Count</th>

                          <th className="border-0 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category) => (
                          <tr key={category._id} className="align-middle">
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div className="category-icon bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-3">
                                  <i className="fas fa-tag"></i>
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold">{category.name}</h6>
                                  <small className="text-muted">ID: {category._id?.substring(0, 8)}...</small>
                                </div>
                              </div>
                            </td>

                            <td className="text-center"><CBadge onClick={()=>{
                              navigate(`/admin/product/list?category=${category.name}`)
                            }} color="success">{category.productCount}</CBadge></td>

                            <td className="text-center categories-actions-cell">
                              <ActionButtons
                                onEdit={() => handleEdit(category)}
                                onDelete={() => handleDelete(category)}
                                onView={() => handleView(category)}
                                showView={true}
                                size="sm"
                                variant="outline"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>

              {categories.length > 0 && (
                <Card.Footer className="bg-light border-0 py-3">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={goToPage}
                    onNextPage={nextPage}
                    onPrevPage={prevPage}
                    onPageSizeChange={changePageSize}
                  />
                </Card.Footer>
              )}
            </Card>
          </Col>
        </Row>
      </Container>



      {/* Add/Edit Category Modal */}
      <CommonModal
        visible={showModal}
        onClose={resetForm}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className={`fas ${editingCategory ? 'fa-edit' : 'fa-plus-circle'} fa-lg`}></i>
            </div>
            <div>
              <h5 className="mb-0">{editingCategory ? 'Edit Category' : 'Add New Category'}</h5>
              <small className="opacity-75">
                {editingCategory ? 'Update existing category details' : 'Create a new product category'}
              </small>
            </div>
          </div>
        }
        size="lg"
        confirmColor="light"
        onConfirm={handleSubmit}
        confirmText={editingCategory ? 'Update' : 'Create'}
        cancelText="Cancel"
        showConfirm={true}
        showCancel={true}
        loading={loading}
        backdrop="static"
        showFooter={false}
        customHeaderClass="modal-header-gradient"
      >
        <div className="p-4">
          <Form onSubmit={handleSubmit}>
            {Object.keys(errors).length > 0 && (
              <Alert variant="danger" className="rounded-3 border-0 shadow-sm">
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-triangle me-3 fs-4"></i>
                  <div>
                    <h6 className="mb-1">Please fix the errors below</h6>
                    <ul className="mb-0">
                      {Object.values(errors).map((error, index) => (
                        <li key={index} className="small">{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Alert>
            )}

            {/* Category Name */}
            <div className="mb-4">
              <label className="form-label fw-semibold text-dark mb-2">
                <i className="fas fa-tag text-primary me-2"></i>
                Category Name
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="fas fa-heading text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control form-control-custom border-start-0"
                  placeholder="e.g., Electronics, Clothing, Food"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: '' });
                  }}
                  style={{ borderLeft: 'none' }}
                />
              </div>
              {/* {errors.name && (
                <div className="text-danger small mt-2 d-flex align-items-center">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {errors.name}
                </div>
              )} */}
              <div className="form-text text-muted mt-2">
                Enter a descriptive name for your category (2-50 characters)
              </div>
            </div>


            {/* Modal Footer Buttons */}
            <div className="modal-footer-custom mt-4 pt-3">
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="outline-secondary"
                  onClick={resetForm}
                  className="btn-action"
                  disabled={submitLoading}
                >
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  className="btn-action"
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {editingCategory ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <i className={`fas ${editingCategory ? 'fa-save' : 'fa-plus-circle'} me-2`}></i>
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </CommonModal>

      {/* View Category Details Modal */}
      <CommonModal
        visible={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className="fas fa-eye fa-lg"></i>
            </div>
            <div>
              <h5 className="mb-0">Category Details</h5>
              <small className="opacity-75">Complete information about this category</small>
            </div>
          </div>
        }
        size="lg"
        showConfirm={false}
        cancelText="Close"
        backdrop="static"
        customHeaderClass="modal-header-gradient"
      >
        {viewingCategory && (
          <div className="p-4">
            {/* Header Section */}
            <div className="text-center mb-4">
              <div className="avatar-circle bg-gradient-primary text-white d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '80px', height: '80px', fontSize: '32px', borderRadius: '50%' }}>
                <i className="fas fa-tag"></i>
              </div>
              <h3 className="fw-bold mb-2 text-dark">{viewingCategory.name}</h3>

            </div>

            <div className="divider mb-4">
              <hr className="border-secondary opacity-25" />
            </div>

            {/* Statistics */}
            <Row className="g-3 mb-4">
              <Col md={4}>
                <div className="stat-card">
                  <div className="stat-icon text-primary">
                    <i className="fas fa-cogs"></i>
                  </div>
                  <h3 className="fw-bold mb-1">{viewingCategory.productCount || 0}</h3>
                  <p className="text-muted mb-0">Total Products</p>
                </div>
              </Col>
              <Col md={4}>
                <div className="stat-card">
                  <div className="stat-icon text-success">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <h6 className="fw-bold mb-1">
                    {new Date(viewingCategory.createdAt).toLocaleDateString()}
                  </h6>
                  <p className="text-muted mb-0">Created On</p>
                </div>
              </Col>
              <Col md={4}>
                <div className="stat-card">
                  <div className="stat-icon text-warning">
                    <i className="fas fa-history"></i>
                  </div>
                  <h6 className="fw-bold mb-1">
                    {new Date(viewingCategory.updatedAt).toLocaleDateString()}
                  </h6>
                  <p className="text-muted mb-0">Last Updated</p>
                </div>
              </Col>
            </Row>

            {/* Machines List */}
            {/* <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold text-dark mb-0">
            <i className="fas fa-list-check text-primary me-2"></i>
            Assigned Machines
          </h6>
          <Badge bg="primary" className="rounded-pill px-3 py-1">
            {viewingCategory.machineIds?.length || 0} Machine{viewingCategory.machineIds?.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {viewingCategory.machineIds?.length > 0 ? (
          <div className="bg-light rounded-3 p-3">
            <div className="row g-2">
              {viewingCategory.machineIds.map((machineId, index) => {
                const machine = machineOptions.find(opt => opt.value === machineId);
                return (
                  <div className="col-md-6" key={index}>
                    <div className="machine-list-item">
                      <div className="d-flex align-items-center">
                        <div className="machine-number me-3">
                          {index + 1}
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">
                            {machine?.label?.split('(')[0]?.trim() || `Machine ${index + 1}`}
                          </h6>
                          <div className="text-muted small">
                            <i className="fas fa-hashtag me-1"></i>
                            ID: {machineId}
                          </div>
                        </div>
                        <Badge bg="light" text="success" className="border">
                          <i className="fas fa-check-circle me-1"></i>
                          Active
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="fas fa-cogs"></i>
            </div>
            <h5 className="text-muted mb-2">No Machines Assigned</h5>
            <p className="text-muted mb-3">
              This category doesn't have any machines assigned yet.
            </p>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => {
                setShowViewModal(false);
                handleEdit(viewingCategory);
              }}
            >
              <i className="fas fa-plus me-2"></i>Assign Machines
            </Button>
          </div>
        )}
      </div> */}
          </div>
        )}
      </CommonModal>

    </>
  );
}

export default Categories;
